import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import {
  DayPicker,
  getDefaultClassNames,
  type ChevronProps,
  type DayButtonProps,
  type DayPickerProps,
} from 'react-day-picker';

import { twcn } from '@web/ui/core/utils';

function CalendarChevron({ orientation }: ChevronProps): React.JSX.Element {
  return orientation === 'left' ? (
    <CaretLeftIcon size={16} aria-hidden />
  ) : (
    <CaretRightIcon size={16} aria-hidden />
  );
}

// day must be excluded (not a valid button DOM prop); className is rebuilt via twcn.
function CalendarDayButton(props: DayButtonProps): React.JSX.Element {
  const { day, modifiers, className, ...rest } = props;
  return (
    <button
      {...rest}
      className={twcn(
        'inline-flex h-row-h w-row-h items-center justify-center rounded-md text-base',
        modifiers.selected
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground hover:bg-muted',
        modifiers.today && !modifiers.selected && 'border border-primary',
        modifiers.outside && 'text-faint-foreground',
        modifiers.disabled && 'opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      )}
    />
  );
}

/**
 * Single-date calendar on react-day-picker v10. shadcn-style, tokenized to the
 * VOLL design system (no arbitrary Tailwind), Phosphor chevrons. Used by the
 * DateTimePicker molecule.
 */
export function Calendar({
  classNames,
  showOutsideDays = true,
  ...props
}: DayPickerProps): React.JSX.Element {
  const defaults = getDefaultClassNames();
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className="p-3"
      classNames={{
        months: 'flex flex-col gap-4',
        month: 'flex flex-col gap-4',
        month_caption: 'flex items-center justify-center relative h-row-h',
        caption_label: 'text-base font-medium capitalize text-foreground',
        nav: 'absolute inset-x-0 top-0 flex items-center justify-between',
        button_previous: twcn(
          defaults.button_previous,
          'inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
        ),
        button_next: twcn(
          defaults.button_next,
          'inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-row-h text-tiny font-normal text-muted-foreground',
        week: 'mt-1 flex w-full',
        day: 'p-0 text-center',
        outside: 'text-faint-foreground',
        disabled: 'text-faint-foreground opacity-50',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  );
}
